import { BringgFontIcons } from '@bringg/bringg-icons';

import { ExtendedMarkerData } from '../../types';

export type GroupedClusterSegmentVariant = 'single-route' | 'multi-neutral' | 'multi-driver';

export type GroupedClusterSegment = {
	key: string;
	icon: BringgFontIcons | string;
	markers: ExtendedMarkerData[];
	variant: GroupedClusterSegmentVariant;
	driverRouteColor?: string;
};

export type GroupedClusterVisualPlan = {
	mode: 'single-route' | 'multi-route';
	shellBackground: string;
	tipColor: string;
	segments: GroupedClusterSegment[];
};

const BLACK = '#000000';

const isTaskMarker = (m: ExtendedMarkerData) => m.data?.type === 'task';

const isDriverMarker = (m: ExtendedMarkerData) => m.data?.type === 'driver';

const unique = <T>(values: T[]) => [...new Set(values)];

const groupMarkersByClusterIcon = (markers: ExtendedMarkerData[]) =>
	markers.reduce((acc, marker) => {
		const iconKey = marker?.data?.clusterIcon || BringgFontIcons.LocationFill;
		const key = String(iconKey);
		if (!acc[key]) {
			acc[key] = [];
		}
		acc[key].push(marker);
		return acc;
	}, {} as Record<string, ExtendedMarkerData[]>);

const groupByDriverRouteColor = (markers: ExtendedMarkerData[]) =>
	markers.reduce((acc, marker) => {
		const colorKey = marker.color ?? '__no_route_color__';
		if (!acc[colorKey]) {
			acc[colorKey] = [];
		}
		acc[colorKey].push(marker);
		return acc;
	}, {} as Record<string, ExtendedMarkerData[]>);

const sortSegments = (a: GroupedClusterSegment, b: GroupedClusterSegment) => {
	const user = String(BringgFontIcons.UserFill);
	const aIcon = String(a.icon);
	const bIcon = String(b.icon);
	if (aIcon === user && bIcon !== user) {
		return 1;
	}
	if (aIcon !== user && bIcon === user) {
		return -1;
	}
	return a.key.localeCompare(b.key);
};

const resolveSingleRouteColor = (
	taskMarkers: ExtendedMarkerData[],
	driverMarkers: ExtendedMarkerData[],
	otherMarkers: ExtendedMarkerData[]
): string | null => {
	if (otherMarkers.length > 0) {
		return null;
	}

	if (taskMarkers.length > 0) {
		const routeIds = unique(taskMarkers.map(m => m.groupId));
		if (routeIds.length !== 1) {
			return null;
		}
		const taskColor = taskMarkers.find(m => m.color)?.color;
		if (!taskColor) {
			return null;
		}
		if (driverMarkers.length > 0 && driverMarkers.some(d => d.color !== taskColor)) {
			return null;
		}
		return taskColor;
	}

	if (driverMarkers.length > 0) {
		const colors = unique(driverMarkers.map(d => d.color).filter(Boolean)) as string[];
		if (colors.length !== 1) {
			return null;
		}
		return colors[0];
	}

	return null;
};

const buildSegments = (markers: ExtendedMarkerData[], isSingleRoute: boolean): GroupedClusterSegment[] => {
	const byIcon = groupMarkersByClusterIcon(markers);
	const segments: GroupedClusterSegment[] = [];
	const userIcon = String(BringgFontIcons.UserFill);

	for (const [icon, iconMarkers] of Object.entries(byIcon)) {
		if (!isSingleRoute && icon === userIcon) {
			for (const [colorKey, colorMarkers] of Object.entries(groupByDriverRouteColor(iconMarkers))) {
				segments.push({
					key: `${icon}-${colorKey}`,
					icon,
					markers: colorMarkers,
					variant: 'multi-driver',
					driverRouteColor: colorKey === '__no_route_color__' ? '#000000' : colorKey
				});
			}
		} else {
			segments.push({
				key: icon,
				icon,
				markers: iconMarkers,
				variant: isSingleRoute ? 'single-route' : 'multi-neutral'
			});
		}
	}

	return segments.sort(sortSegments);
};

export const buildGroupedClusterVisualPlan = (
	markersData: (ExtendedMarkerData | undefined)[]
): GroupedClusterVisualPlan => {
	const markers = markersData.filter((m): m is ExtendedMarkerData => Boolean(m));

	const { taskMarkers, driverMarkers, otherMarkers } = markers.reduce(
		(acc, m) => {
			if (isTaskMarker(m)) acc.taskMarkers.push(m);
			else if (isDriverMarker(m)) acc.driverMarkers.push(m);
			else acc.otherMarkers.push(m);
			return acc;
		},
		{
			taskMarkers: [] as ExtendedMarkerData[],
			driverMarkers: [] as ExtendedMarkerData[],
			otherMarkers: [] as ExtendedMarkerData[]
		}
	);

	const singleRouteColor = resolveSingleRouteColor(taskMarkers, driverMarkers, otherMarkers);

	if (singleRouteColor) {
		return {
			mode: 'single-route',
			shellBackground: singleRouteColor,
			tipColor: singleRouteColor,
			segments: buildSegments(markers, true)
		};
	}

	return {
		mode: 'multi-route',
		shellBackground: BLACK,
		tipColor: BLACK,
		segments: buildSegments(markers, false)
	};
};
